import { STATIC_FIELD_TYPES, conditionMet, type FormFieldDef } from '@hcc/types';
import { ChevronDown, Paperclip } from 'lucide-react';
import { useFormVersion } from '../../hooks/useOnboardingForms';
import { FieldView } from './formfields/FieldView';
import type { FieldValue } from './formfields/formValues';

/** One submitted form's stored shape (from metadata.onboarding.forms[key]). */
interface Submission {
  values?: Record<string, FieldValue>;
  files?: Record<string, { name: string; key: string; content_type: string }[]>;
  consent?: boolean;
  form_version_id?: string;
  captured_at?: string;
}

/**
 * HR review of submitted forms. Renders each form's answers against the
 * version schema it was filled against (so labels and order match), reusing
 * the shared FieldView read-only. File answers become download links to the
 * HR-gated route. Only ever rendered inside the HR/admin full view.
 */
export function PersonFormSubmissions({
  personId,
  forms,
}: {
  personId: string;
  forms: Record<string, Submission>;
}) {
  const entries = Object.entries(forms);
  if (entries.length === 0) return null;
  return (
    <div className="space-y-3">
      <div className="text-xs font-medium uppercase tracking-wider text-fg-dim">
        Form submissions (HR only)
      </div>
      {entries.map(([key, sub]) => (
        <PersonFormSubmission key={key} personId={personId} formKey={key} submission={sub} />
      ))}
    </div>
  );
}

function PersonFormSubmission({
  personId,
  formKey,
  submission,
}: {
  personId: string;
  formKey: string;
  submission: Submission;
}) {
  const version = useFormVersion(submission.form_version_id);

  return (
    <details className="group rounded-token-sm border border-line/10 bg-surface-2">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-2 p-3 hover:bg-surface-inset/40">
        <div className="font-medium text-fg">{version.data?.title ?? formKey}</div>
        <div className="flex items-center gap-2">
          {submission.captured_at ? (
            <span className="text-xs text-fg-dim">
              {new Date(submission.captured_at).toLocaleDateString()}
            </span>
          ) : null}
          <ChevronDown className="h-4 w-4 text-fg-muted transition-transform group-open:rotate-180" aria-hidden />
        </div>
      </summary>
      <div className="border-t border-line/10 p-3">
        <FormAnswers
          personId={personId}
          versionId={submission.form_version_id}
          values={submission.values ?? {}}
          files={submission.files ?? {}}
          consent={submission.consent}
        />
      </div>
    </details>
  );
}

/**
 * Read-only render of a set of form answers against the schema version they
 * were filled against, so labels and order match. Shared by the submitted-form
 * review and the in-progress draft viewer. File answers become HR-gated
 * download links; the draft viewer passes no files.
 */
export function FormAnswers({
  personId,
  versionId,
  values,
  files,
  consent,
}: {
  personId: string;
  versionId?: string;
  values: Record<string, FieldValue>;
  files: Record<string, { name: string; key: string; content_type: string }[]>;
  consent?: boolean;
}) {
  const version = useFormVersion(versionId);
  const pages = version.data?.schema.pages ?? [];
  const hasFields = pages.some((p) => p.fields.length > 0);

  if (version.isLoading) return <p className="text-sm text-fg-muted">Loading answers…</p>;
  if (!hasFields) {
    // Fallback when the schema can't be resolved: raw key/value dump.
    return (
      <div className="space-y-1 text-sm text-fg">
        {Object.entries(values).map(([k, v]) => (
          <div key={k}>
            <span className="text-fg-muted">{k.replace(/_/g, ' ')}:</span> {String(v)}
          </div>
        ))}
      </div>
    );
  }

  const renderField = (f: FormFieldDef) =>
    f.type === 'signature' ? (
      <div key={f.key} className="text-sm">
        <span className="font-medium text-fg-muted">{f.label}</span>
        <div className="mt-1">
          {(files[f.key] ?? []).length === 0 ? (
            <span className="text-fg-dim">Not signed</span>
          ) : (
            (files[f.key] ?? []).map((file) => (
              <img
                key={file.key}
                src={`/api/admin/onboarding/people/${personId}/files/${file.key}`}
                alt={f.label}
                className="max-h-28 rounded-token-sm border border-line/10 bg-white"
              />
            ))
          )}
        </div>
      </div>
    ) : f.type === 'file_upload' ? (
      <div key={f.key} className="text-sm">
        <span className="font-medium text-fg-muted">{f.label}</span>
        <div className="mt-1 space-y-1">
          {(files[f.key] ?? []).length === 0 ? (
            <span className="text-fg-dim">No file uploaded</span>
          ) : (
            (files[f.key] ?? []).map((file) => (
              <a
                key={file.key}
                href={`/api/admin/onboarding/people/${personId}/files/${file.key}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1.5 text-accent hover:underline"
              >
                <Paperclip className="h-3.5 w-3.5" />
                {file.name}
              </a>
            ))
          )}
        </div>
      </div>
    ) : (
      <FieldView key={f.key} field={f} value={values[f.key] ?? null} onChange={() => {}} disabled />
    );

  // Render page by page so a multi-page form keeps its structure instead of
  // collapsing into one long, confusing list. Pages with no answerable fields
  // are dropped first so the "Page N" numbering and dividers stay sequential.
  // Hide whole pages and individual fields whose visible_if isn't met, using the
  // same evaluator the inductee renderer uses, so HR sees exactly what the
  // inductee saw instead of every irrelevant "No"-path question.
  const visiblePages = pages
    .filter((page) => conditionMet(page.visible_if, values))
    .map((page) => ({
      page,
      fields: page.fields.filter(
        (f) => !STATIC_FIELD_TYPES.includes(f.type) && conditionMet(f.visible_if, values),
      ),
    }))
    .filter((p) => p.fields.length > 0);
  const multiPage = visiblePages.length > 1;

  return (
    <div className="space-y-6">
      {visiblePages.map(({ page, fields }, idx) => (
        <div
          key={idx}
          // A divider above every page after the first makes the page break obvious.
          className={idx > 0 ? 'border-t-2 border-line-strong/20 pt-6' : ''}
        >
          {multiPage ? (
            <div className="mb-3">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-accent">
                Page {idx + 1} of {visiblePages.length}
              </div>
              {page.title ? <div className="text-sm font-semibold text-fg">{page.title}</div> : null}
              {page.description ? (
                <p className="mt-0.5 text-xs text-fg-muted">{page.description}</p>
              ) : null}
            </div>
          ) : null}
          <div className="space-y-3">{fields.map(renderField)}</div>
        </div>
      ))}
      {consent ? (
        <div className="text-xs text-success-soft-fg">✓ Consent given for special-category data</div>
      ) : null}
    </div>
  );
}
